import { GIF_MIN_DELAY_MS } from '../constants';
import { ConversionError } from './errors';

export type GifMetadata = Readonly<{
  width: number;
  height: number;
  frameCount: number;
  normalizedDelays: readonly number[];
  normalizedDurationMs: number;
}>;

function skipSubBlocks(bytes: Uint8Array, initialOffset: number): number {
  let offset = initialOffset;
  while (offset < bytes.length) {
    const length = bytes[offset];
    if (length === undefined) break;
    offset += 1;
    if (length === 0) return offset;
    if (offset + length > bytes.length) break;
    offset += length;
  }
  throw new ConversionError('invalid-file', 'The GIF contains a truncated data block.');
}

function requireBytes(bytes: Uint8Array, offset: number, count: number): void {
  if (offset + count > bytes.length) {
    throw new ConversionError('invalid-file', 'The GIF is truncated.');
  }
}

export function parseGifMetadata(bytes: Uint8Array): GifMetadata {
  requireBytes(bytes, 0, 13);
  const header = String.fromCharCode(...bytes.subarray(0, 6));
  if (header !== 'GIF87a' && header !== 'GIF89a') {
    throw new ConversionError('invalid-file', 'The selected file is not a valid GIF.');
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = view.getUint16(6, true);
  const height = view.getUint16(8, true);
  if (width === 0 || height === 0) {
    throw new ConversionError('invalid-file', 'The GIF has invalid dimensions.');
  }

  const packed = bytes[10] ?? 0;
  let offset = 13;
  if ((packed & 0x80) !== 0) offset += 3 * 2 ** ((packed & 0x07) + 1);
  requireBytes(bytes, 0, offset);

  const delays: number[] = [];
  let pendingDelayMs = 100;
  let foundTrailer = false;

  while (offset < bytes.length) {
    const marker = bytes[offset++];
    if (marker === 0x3b) {
      foundTrailer = true;
      break;
    }

    if (marker === 0x21) {
      requireBytes(bytes, offset, 1);
      const label = bytes[offset++];
      if (label === 0xf9) {
        requireBytes(bytes, offset, 6);
        if (bytes[offset] !== 4 || bytes[offset + 5] !== 0) {
          throw new ConversionError('invalid-file', 'The GIF has an invalid control block.');
        }
        pendingDelayMs = view.getUint16(offset + 2, true) * 10;
        offset += 6;
      } else {
        offset = skipSubBlocks(bytes, offset);
      }
      continue;
    }

    if (marker === 0x2c) {
      requireBytes(bytes, offset, 9);
      const descriptorPacked = bytes[offset + 8] ?? 0;
      offset += 9;
      if ((descriptorPacked & 0x80) !== 0) {
        offset += 3 * 2 ** ((descriptorPacked & 0x07) + 1);
      }
      requireBytes(bytes, offset, 1);
      offset += 1;
      offset = skipSubBlocks(bytes, offset);
      delays.push(Math.max(GIF_MIN_DELAY_MS, pendingDelayMs));
      pendingDelayMs = 100;
      continue;
    }

    throw new ConversionError('invalid-file', 'The GIF contains an unknown block marker.');
  }

  if (!foundTrailer || delays.length === 0) {
    throw new ConversionError('invalid-file', 'The GIF is incomplete or has no image frame.');
  }

  return {
    width,
    height,
    frameCount: delays.length,
    normalizedDelays: delays,
    normalizedDurationMs: delays.reduce((total, delay) => total + delay, 0),
  };
}
